/**
 * Mirrors `backend/src/ai-ops/ai-reliability.types.ts`.
 *
 * Provider health, why a call fell back, and what the models cost. Keep this
 * file in step with the backend one — nothing here is frontend-only.
 */

/** What a provider is being asked to do when it is checked */
export type AiCapability = 'text' | 'webSearch' | 'image';

/** Which part of the app spent the money */
export type AiFeature =
  | 'monitoring_scan'
  | 'monitoring_digest'
  | 'intelligence_report'
  | 'intelligence_news'
  | 'intelligence_ask'
  | 'intelligence_brief'
  | 'intelligence_discover'
  | 'intelligence_influence'
  | 'issue_assess'
  | 'issue_brief'
  | 'government_structure'
  | 'government_officials'
  | 'government_verify'
  | 'government_match'
  | 'government_duplicates'
  | 'business_card'
  | 'connection_test'
  | 'other';

/* ------------------------------------------------------------------ */
/* Health                                                              */
/* ------------------------------------------------------------------ */

export interface ProviderCheck {
  capability: AiCapability;
  model: string | null;
  ok: boolean;
  message: string | null;
  latencyMs: number;
  checkedAt: string | null;
  consecutiveFailures: number;
  /** When it last worked, so an outage can be dated */
  lastOkAt: string | null;
}

export type ProviderStatus = 'ok' | 'degraded' | 'failing' | 'unchecked' | 'unconfigured';

export interface ProviderHealth {
  provider: string;
  label: string;
  /** A key is saved for this provider */
  configured: boolean;
  /** Which jobs this provider is currently assigned to */
  assignedTo: AiFeature[] | string[];
  checks: ProviderCheck[];
  /** Worst state across the checks, for the one-line summary */
  status: ProviderStatus;
}

export interface HealthReport {
  providers: ProviderHealth[];
  /** True when at least one provider can answer a research question */
  researchReady: boolean;
  /** True when something the app depends on is broken right now */
  needsAttention: boolean;
  checkedAt: string | null;
  nextCheckAt: string | null;
}

/* ------------------------------------------------------------------ */
/* Fallback                                                            */
/* ------------------------------------------------------------------ */

/**
 * Why a provider was skipped or retried. Only failures that another provider
 * could plausibly answer are worth falling back on — a rejected key or a
 * missing model, never a cancelled request or a refusal to answer.
 */
export type FallbackReason =
  | 'auth'
  | 'model_missing'
  | 'rate_limited'
  | 'server_error'
  | 'timeout'
  | 'unreachable';

export interface AttemptRecord {
  provider: string;
  model: string;
  ok: boolean;
  reason?: FallbackReason;
  message?: string;
  durationMs: number;
}

/** What a call did, including everything it tried before it worked */
export interface CallOutcome {
  attempts: AttemptRecord[];
  provider: string | null;
  model: string | null;
  usedFallback: boolean;
}

/* ------------------------------------------------------------------ */
/* Cost                                                                */
/* ------------------------------------------------------------------ */

export interface UsageBucket {
  /** ISO date for a day, or `YYYY-MM` for a month */
  period: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costCents: number;
  failures: number;
}

export type FeatureBucket = UsageBucket & { feature: AiFeature };
export type ProviderBucket = UsageBucket & { provider: string };

export interface UsageSummary {
  /** The month the budget is measured against */
  month: string;
  totalCostCents: number;
  totalCalls: number;
  failures: number;
  byFeature: FeatureBucket[];
  byProvider: ProviderBucket[];
  daily: UsageBucket[];
  budget: AiBudget;
  /** Percentage of the monthly ceiling spent, 0 when no ceiling is set */
  spentPercent: number;
}

/**
 * What the app may spend before it stops asking models anything.
 *
 * A ceiling of 0 means no limit. Scheduled work is stopped first and
 * interactive work last, so a runaway cron cannot take the button a person
 * is about to press.
 */
export interface AiBudget {
  monthlyCapCents: number;
  /** Stop scheduled work at this share of the cap, 0–1 */
  pauseScheduledAt: number;
  /** Warn admins at this share */
  warnAt: number;
}

export const DEFAULT_BUDGET: AiBudget = {
  monthlyCapCents: 0,
  pauseScheduledAt: 0.8,
  warnAt: 0.6,
};

/** What a caller is told when the ceiling has been reached */
export interface BudgetState {
  allowed: boolean;
  spentCents: number;
  capCents: number;
  /** Plain language: what is stopped and what would restart it */
  reason?: string;
}
